import _WidgetBaseSP from 'more/_WidgetBaseSP';
import lang from 'dojo/_base/lang';
import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './Kanban.html?raw';
import on from 'dojo/on';
import Sortable from 'sortablejs/Sortable';
import type { SortableEvent } from "sortablejs";
import { KanbanTypes } from "./KanbanTypes";
import { ColorHelpers, EntityHelpers, HtmlHelpers, StringHelpers, ToasterHelpers } from "../../../more/Helpers";
import EntityStore from '../../Tools/EntityStore';
import Button from '../Button/Button';
import { EntityViewTypes } from "../EntityBased/EntityView/EntityViewTypes";
import GenericEntityView from '../EntityBased/EntityView/GenericEntityView';

class Kanban extends compose(_WidgetBaseSP) {
    templateString: any = template;

    /* HTML Points */
    private dashboardsContainerNode: HTMLDivElement;

    /* Variables */
    private _selectedKanbanElement: KanbanTypes.DataElement = null;

    private leftWidth: string = "300px";
    private infoColumnWidth: string = "300px";
    private defaultContainerHeight: string = "400px";


    private columnsReg: Record<string, KanbanTypes.ColumnConfig>;

    private dashboardsReg: Record<string, KanbanTypes.Dashboard>;
    private tableContainer: KanbanTypes.TableContainer;

    private infoContentAPI: string;
    private dataChangeCallback: KanbanTypes.DataChangeCallback;
    private dataDetailsCallback: KanbanTypes.ElementClickCallback;
    private editorRequestCallback: KanbanTypes.ElementClickCallback;
    private editBtnEnabledCallback: KanbanTypes.EnableBtnCallback;
    private deleteBtnEnabledCallback: KanbanTypes.EnableBtnCallback;

    private dataStore: MdsLax<InstanceType<typeof EntityStore<KanbanTypes.StoreData>>>;

    private readonly: boolean = false;

    private getDataBySourceId(column: KanbanTypes.Column, sourceItemID: string): KanbanTypes.DataElement {
        if (!column || !sourceItemID) return null; // es kann sein, dass column leer ist (zb weil sourceColumn garnicht verfügbar)

        for (let element of column.dataElements) {
            if (element.sourceItemID === sourceItemID) {
                return element;
            } 
        }
        return null;
    }

    private getColumnOfDashboard(dashboard: KanbanTypes.Dashboard, columnID: string) {
        return dashboard.columnsReg[columnID];
    }

    private drawDataElement(dataElement: KanbanTypes.DataElement, targetColumn: KanbanTypes.Column) {
        const dataElementNode = this.UiModder.domConstruct.create("div", {
            className: "kanbanDataElement",
            "data-id": dataElement.sourceItemID,
            "dashboard-id": targetColumn.dashboard?.ID,
        }, targetColumn.contentNode);
        
        if (dataElement.color) {
            dataElementNode.style.setProperty("--elementColor", dataElement.color);
        }
        if (dataElement.secondaryColor) {
            dataElementNode.style.setProperty("--elementColorSecondary", dataElement.secondaryColor);
        } else if (dataElement.color) {
            if (ColorHelpers.isDarkColor(dataElement.color)) {
                this.UiModder.domClass.add(dataElementNode, "darkColor");
            }
        }

        let bCanDrag = true;
        if ((dataElement as KanbanTypes.SourceDataElement).isUsed) {
            this.UiModder.domClass.add(dataElementNode, "inUse");
            bCanDrag = false;
        }
        if (dataElement.disabled === true) {
            this.UiModder.domClass.add(dataElementNode, "disabled");
            bCanDrag = false;
        } 
        if (dataElement.locked === true) {
            this.UiModder.domClass.add(dataElementNode, "locked");
            bCanDrag = false;
        }

        if (this.readonly || targetColumn.readonly || targetColumn.dashboard.readonly) {
            bCanDrag = false;
        }
        

        if (bCanDrag == true) {
            this.UiModder.domClass.add(dataElementNode, "draggable");
        }
        let label = this.UiModder.domConstruct.create("label", {
            innerHTML: dataElement.label ? dataElement.label : "?label? " + dataElement.sourceItemID,
            className:"dataElementLabel"
        }, dataElementNode, "first");

        if (dataElement.secondaryLabel) {
            this.UiModder.domConstruct.create("label", {
                innerHTML: dataElement.secondaryLabel,
                className: "secondaryLabel"
            }, label);
        }

        let buttonContainer = this.UiModder.domConstruct.create("div", {
            className: "dataElementActionContainer"
        }, dataElementNode);
        dataElement.actionContainerNode = buttonContainer;


        if (targetColumn.dashboard.infoColumn) {
            on(label, "click", lang.hitch(this, this._onClick_DataElement, dataElement));
            this.UiModder.domClass.add(label, "clickable hoverDark");
        }      

        dataElement.node = dataElementNode;
        this._drawDataElementButtons(dataElement);
    }

    private _drawDataElementButtons(dataElement: KanbanTypes.DataElement) {
        let buttonContainer = dataElement.actionContainerNode;

        if (!buttonContainer) return;

        this.UiModder.domConstruct.empty(buttonContainer);

        let bReadonly = this.readonly || dataElement.column.readonly || dataElement.column.dashboard.readonly;

        if (!dataElement.disabled && !bReadonly && dataElement.column.isSourceColumn != true) {
            if (this.editorRequestCallback && dataElement) {
                let editorBtn = this.UiModder.domConstruct.create("button", {
                    className: "buttonBase actionBtn editorBtn",
                    type: "editButton"
                }, buttonContainer);

                HtmlHelpers.createFontawesomeIcon("fas fa-edit", null, editorBtn);

                on(editorBtn, "click", lang.hitch(this, this._onClick_Editor, dataElement));
            }
            if (dataElement.removable !== false) {
                let btn = this.UiModder.domConstruct.create("button", {
                    className: "buttonBase actionBtn removeBtn",
                    type: "removeButton"
                }, buttonContainer);

                HtmlHelpers.createFontawesomeIcon("fas fa-times", null, btn);

                on(btn, "click", lang.hitch(this, this._onClick_RemoveDataElement, dataElement));
            }
            
        }
    }

    private _handleButtonsReadonly(dataElement: KanbanTypes.DataElement) {
        this._handleEditButtonReadonly(dataElement);
        this._handleDeleteButtonReadonly(dataElement);
    }

    private _handleEditButtonReadonly(dataElement: KanbanTypes.DataElement) {
        if (!this.editBtnEnabledCallback) return;
        let button = dataElement.actionContainerNode.querySelector('button[type=editButton]');
        if (!button) return;
        this.disableNode(button, !this.editBtnEnabledCallback(dataElement));
    }

    private _handleDeleteButtonReadonly(dataElement: KanbanTypes.DataElement) {
        if (!this.deleteBtnEnabledCallback) return;
        let button = dataElement.actionContainerNode.querySelector('button[type=deleteButton]');
        if (!button) return;
        this.disableNode(button, !this.deleteBtnEnabledCallback(dataElement));
    }



    private _onClick_Editor(dataElement: KanbanTypes.DataElement, evt: PointerEvent) {
        if (!this.editorRequestCallback) return;

        evt.stopPropagation();
        evt.preventDefault();
        
        this.editorRequestCallback(dataElement, evt);
    }
    private _onClick_RemoveDataElement(dataElement: KanbanTypes.DataElement, evt: MouseEvent) {
        let targetColumn = dataElement.column;
        let dashboard = targetColumn.dashboard;

        evt.stopPropagation();
        evt.preventDefault();

        // gibts im Datenpool dieses Element?
        let dataPoolElement = this.getDataBySourceId(dashboard.sourceData, dataElement.sourceItemID) as KanbanTypes.SourceDataElement;
        if (dataPoolElement && dataPoolElement.isUsed === true) {
            this.setDataElementToInUse(dashboard.sourceData, dataElement.sourceItemID, false);

            try {
                let scrollingNode = dataPoolElement.node.parentElement;
                let y = dataPoolElement.node.scrollTop;
                scrollingNode.scrollTo({ top: y, behavior: "smooth" });
            } catch (ex) {

            }
            //dataPoolElement.node.scrollIntoView({ behavior: "smooth" });
        }


        if (!dataElement.assignmentID) {
            // ???
            return;
        }

        let bWasExisting = this.dataStore.isEntityNew(dataElement.assignmentID) != true;

        // falls das Element neu ist:
        // durch das Rausziehen eines Datapool-Elements in einen anderen Container haben wir das Klickevent immer noch 
        // vom Datapool-Element picken, weil Sortable die originale Node rübergezogen hat und im Datapool die Kopie anlegt.
        // In Wirklichkeit wollen wir natürlich das Dataelement aus dem Targetcontainer haben!
        if (dataPoolElement && !bWasExisting) {
            let actualTarget = this.getDataBySourceId(targetColumn, dataElement.sourceItemID);
            if (actualTarget) dataElement = actualTarget;
        }


        this.dataStore.deleteEntry(dataElement.assignmentID);


        for (let i = 0; i < targetColumn.dataElements.length; i++) {
            let columnEntry = targetColumn.dataElements[i]
            if (columnEntry.assignmentID == dataElement.assignmentID) {
                targetColumn.dataElements.splice(i, 1);

                if (bWasExisting) {
                    this.UiModder.domClass.add(dataElement.node, "removed");
                } else {
                    this.UiModder.domConstruct.destroy(dataElement.node);
                }
                break;
            }
        }
    }

    private _onClick_DataElement(dataElement: KanbanTypes.DataElement, evt: PointerEvent) {
        let dashboard = dataElement.column.dashboard;
        if (dashboard.selectedElement) {
            this.UiModder.domClass.remove(dashboard.selectedElement.node, "selected");
        }

        dashboard.selectedElement = dataElement;
        

        if (dashboard.infoColumn) {
            dashboard.infoColumn.selectedElement = dataElement;
            this.UiModder.domClass.add(dashboard.selectedElement.node, "selected");

            this.drawInfoContent(dashboard);
        }
        else if (this.dataDetailsCallback) {
            //    let buttonContainer = this.UiModder.domConstruct.create("div", {
            //        className: "dataElementActionContainer"
            //    }, dataElementNode);

            //    let btn = this.UiModder.domConstruct.create("button", {
            //        className: "buttonBase actionBtn"
            //    }, buttonContainer);

            //    HtmlHelpers.createFontawesomeIcon("fas fa-info-circle", null, btn);

            //    on(btn, "click", lang.hitch(this, this._onClick_DataElement, dataElement));
            this.dataDetailsCallback(dataElement, evt);
        }
        
    }

    private drawInfoContent(dashboard: KanbanTypes.Dashboard) {
        this.UiModder.domConstruct.empty(dashboard.infoColumn.node);
        
        if (dashboard.infoColumn && dashboard.infoColumn.selectedElement) {

            let dataElement = dashboard.infoColumn.selectedElement;

            let container = this.UiModder.domConstruct.create("div", {
                className: "infoContainer"
            }, dashboard.infoColumn.node);

            if (dataElement.color) {
                container.style.setProperty("--elementColor", dataElement.color);
            }
            if (dataElement.secondaryColor) {
                container.style.setProperty("--elementColorSecondary", dataElement.secondaryColor);
            } else if (dataElement.color) {
                if (ColorHelpers.isDarkColor(dataElement.color)) {
                    this.UiModder.domClass.add(container, "darkColor");
                }
            }

            let label = this.UiModder.domConstruct.create("label", {
                className: "",
                innerHTML: dataElement.label
            }, container);

            if (dataElement.secondaryLabel) {
                this.UiModder.domConstruct.create("label", {
                    className: "secondary",
                    innerHTML: dataElement.secondaryLabel
                }, label);
            }
            let buttonContainer = this.UiModder.domConstruct.create("div", {
                className: "actionButtonContainer"
            }, container);

            if (this.dataDetailsCallback) {
                let button = new Button();
                button.setClassesEx({ defaultClasses: "actionBtn" });
                button.setIcon("fas fa-info");
                button.setClick(lang.hitch(this, this.dataDetailsCallback, dataElement));
                button.placeAt(buttonContainer);
            }

            if (this.editorRequestCallback && dataElement.column.isSourceColumn != true) {
                let button = new Button();
                button.setClassesEx({ defaultClasses: "actionBtn" });
                button.setIcon("fas fa-edit");
                button.setClick(lang.hitch(this, this.editorRequestCallback, dataElement));
                button.placeAt(buttonContainer);
            }
            let content = this.UiModder.domConstruct.create("div", {
                className: "infoContent"
            }, container);
            dashboard.infoColumn.infoContentNode = content;

            if (this.infoContentAPI) {

                this._loadInfoContent(dashboard, dataElement);
            } else if (dataElement.infoContent) {
                this.UiModder.domAttr.set(content, "innerHTML", dataElement.infoContent);
            }
        }

    }
    private _currentInfoContentRequest: Record<string, { handle: dojo.request.Promise<any>, assignmentID: string }>;

    private _loadInfoContent(dashboard: KanbanTypes.Dashboard, dataElement: KanbanTypes.DataElement) {

        let dashboardRequest = this._currentInfoContentRequest[dashboard.ID];
        if (dashboardRequest) {
            if (dataElement.assignmentID != dashboardRequest.assignmentID) {
                dashboardRequest.handle.cancel();
            } else {
                // warten!
                return;
            }
        }

        let verifiedAss: string = null;

        if (this.dataStore.isEntityExisting(dataElement.assignmentID)) {
            verifiedAss = dataElement.assignmentID;
        }

        let simpleData: Partial<KanbanTypes.DataElement> & { dashboardID: string, _target: string } = {
            dashboardID: dashboard.ID,
            _target: "infocontent",
            sourceItemID: dataElement.sourceItemID,
            assignmentID: verifiedAss,
            columnID: dataElement.columnID
        };

        let handle = this.RequestManager.callfunction(
            this.infoContentAPI,
            simpleData,
            lang.hitch(this, this._onInfoRequestLoaded, dashboard, dataElement),
            lang.hitch(this, this._onInfoRequestError, dashboard, dataElement)
        );

        this.StandbyStart(dashboard.infoColumn.infoContentNode);
        this._currentInfoContentRequest[dashboard.ID] = {
            handle: handle,
            assignmentID: dataElement.assignmentID
        };
    }
    private _onInfoRequestLoaded(dashboard: KanbanTypes.Dashboard, dataElement: KanbanTypes.DataElement, response: string | EntityViewTypes.EntityViewResponse<any>) {
        this.StandbyStop();
        this._currentInfoContentRequest[dashboard.ID] = null;

        if (typeof (response) == "string") {
            this.UiModder.domAttr.set(dashboard.infoColumn.infoContentNode, "innerHTML", response);
        } else if (typeof (response) == "object") {
            let evData = response as EntityViewTypes.EntityViewResponse<any>;
            if (evData.content) {
                let entityview = new GenericEntityView();
                entityview.setupWithoutEntity(evData, evData.label ? evData.label : dataElement.label, dashboard.infoColumn.infoContentNode);
            }
        }
    }
    private _onInfoRequestError(dashboard: KanbanTypes.Dashboard, dataElement: KanbanTypes.DataElement, error: any) {
        this.StandbyStop();
        this._currentInfoContentRequest[dashboard.ID] = null;
    }

    setDataElementData<T = any>(dataElement: KanbanTypes.DataElement<T>, newData: T) {
        this.changeDataElement(dataElement, newData);
    }
    changeDataElement<T = any>(dataElement: KanbanTypes.DataElement<T>, newData: T) {
        let currentEntry = this.dataStore.getEntityById(dataElement.assignmentID);
        if (!currentEntry) {
            console.warn(this.id + ": change invalid data element", dataElement, newData);
            return;
        }

        currentEntry.changedData = newData;
        this.dataStore.changeEntry(currentEntry);
        
        this.UiModder.domClass.toggle(dataElement.node, "dirty", this.dataStore.isEntityChanged(currentEntry));

        this._handleButtonsReadonly(dataElement);
    }


    private setDataElementToInUse(column: KanbanTypes.SourceColumn, sourceDataID: string, targetValue?: boolean): void {

        if (typeof (targetValue) != "boolean") {
            targetValue = true;
        }

        let dataElement = this.getDataBySourceId(column, sourceDataID) as KanbanTypes.SourceDataElement;;
        
        if (dataElement) {
            dataElement.isUsed = targetValue;
            this.UiModder.domClass.toggle(dataElement.node, "inUse", dataElement.isUsed);
            this.UiModder.domClass.toggle(dataElement.node, "draggable", !dataElement.isUsed && !this.readonly && !column.readonly && !column.dashboard.readonly);
        }
    }

   

    private _sortable_CloneKanbanElement(dashboard: KanbanTypes.Dashboard, evt: SortableEvent): void {
        
        console.debug(this.id + " _sortable_CloneKanbanElement", "from: " + evt.from.dataset.id, "to: " + evt.to.dataset.id, dashboard, evt);

        const columnSource = this.getColumnOfDashboard(dashboard, evt.from.dataset.id);
        const columnTarget = this.getColumnOfDashboard(dashboard, evt.to.dataset.id);
        let sourceDataElement: KanbanTypes.DataElement = this.getDataBySourceId(dashboard.sourceData, evt.item.dataset.id);

        //ToasterHelpers.showToast("_sortable_CloneKanbanElement" + " | elem: " + sourceDataElement.label + " | from col: " + columnSource?.label + " | to col: " + columnTarget.label, );

        

        if (columnTarget && sourceDataElement) {
            if (columnSource?.isSourceColumn) {
                
                let copyDataElement: KanbanTypes.DataElement = {//lang.clone(element) as KanbanTypes.DataElement;
                    ...sourceDataElement,
                    assignmentID: StringHelpers.randomID(),
                    column: columnTarget,
                    node: sourceDataElement.node
                };

                //this.UiModder.domAttr.set(copy.node, "data-id", copy.sourceItemID);

                // sortablejs hat eine kopie der Node angelegt am ursprünglichen Platz!
                // d.h. das ursprüngliche Element erhält jetzt die clone-Node und umgekehrt
                sourceDataElement.node = evt.clone;
                sourceDataElement.actionContainerNode = sourceDataElement.node.querySelector(".dataElementActionContainer");

                // PROBLEM: das Klickevent für removeData verweist auf den falschen Datensatz
                // die Buttons gehören also neu gemacht
                this._drawDataElementButtons(sourceDataElement);
                this._drawDataElementButtons(copyDataElement);


                this.UiModder.domClass.add(copyDataElement.node, "new");

                on(copyDataElement.node, "click", lang.hitch(this, this._onClick_DataElement, copyDataElement));

                // in die neue Column aufnehmen
                columnTarget.dataElements.splice(evt.newIndex, 0, copyDataElement);
                this.dataStore.addEntry({
                    assignmentID: copyDataElement.assignmentID,
                    columnID: columnTarget.ID,
                    sourceItemID: copyDataElement.sourceItemID,
                    order: evt.newIndex,
                })

                this._handleButtonsReadonly(copyDataElement);
            }
        }
    }

    private _sortable_MoveDataElement(dashboard: KanbanTypes.Dashboard, evt: SortableEvent): void {
        console.debug(this.id + " _sortable_MoveDataElement", "from: " + evt.from.dataset.id, "to: " + evt.to.dataset.id, dashboard, evt);


        const columnSource = this.getColumnOfDashboard(dashboard, evt.from.dataset.id);
        const columnTarget = this.getColumnOfDashboard(dashboard, evt.to.dataset.id);
        if (columnSource && columnTarget) {
            
            const element = this.getDataBySourceId(columnSource, evt.item.dataset.id);

            //ToasterHelpers.showToast("_sortable_MoveDataElement" + " | elem: " + element?.label + " | from col: " + columnSource?.label + " | to col: " + columnTarget?.label);


            if (columnTarget && element) {

                columnTarget.dataElements.splice(evt.newIndex, 0, element);
                columnSource.dataElements.splice(evt.oldIndex, 1);

                element.column = columnTarget;
                element.wasMoved = true;

                this.dataStore.addEntry({
                    assignmentID: element.assignmentID,
                    columnID: columnTarget.ID,
                    sourceItemID: element.sourceItemID,
                    order: evt.newIndex,
                });

                this.UiModder.domClass.add(element.node, "dirty");

                this._sortKanbanById(columnTarget);
                this._sortKanbanById(columnSource);
                this._handleButtonsReadonly(element);
            }
        } else if (columnTarget) {
            // es kommt aus dem Datapool und ist von dort RAUSgezogen worden (kein clone!)
            let element = this.getDataBySourceId(dashboard.sourceData, evt.item.dataset.id);
            if (element) {
                columnTarget.dataElements.splice(evt.newIndex, 0, element);
                element.column = columnTarget;
                this._sortKanbanById(columnTarget);
                this._sortKanbanById(dashboard.sourceData);
                this._handleButtonsReadonly(element);
            }
        }

        
    }

    private _sortKanbanById(kanban: KanbanTypes.Column): void {
        if (kanban) {
            for (let i = 0; i < kanban.dataElements.length; i += 1) {
                kanban.dataElements[i].order = i;
            }
        }
    }

    

    private _kanbanHeadCallback: (dashboard: KanbanTypes.Dashboard) => any;
    private _onClick_KanbanHead(dashboard: KanbanTypes.Dashboard, event: PointerEvent): void {
        event.preventDefault();
        //console.log("kanbanElements", this.kanbanElements);
        //console.log("kanbans", this.kanbanList);

        this._kanbanHeadCallback(dashboard);
    }


    private _createKanbanContainerMain(dashboard: KanbanTypes.Dashboard) {

        
        const kanbanMain = this.UiModder.domConstruct.create("div", {
            className: "kanbanMain",
            style: {
                order: 10
            }
        }, dashboard.innerContainerNode);


        dashboard.scrollingContainer = kanbanMain;


        on(kanbanMain, "scroll", lang.hitch(this, this._scrollHandler, dashboard.ID));

        const collapsing = this.UiModder.domConstruct.create("div", {
            className: "collapsing"
        }, kanbanMain);

        const collapsingWrapper = this.UiModder.domConstruct.create("div", {
            className: "collapsingWrapper"
        }, collapsing);


        const kanbanInnerMain = this.UiModder.domConstruct.create("div", {
            className: "kanbanInnerMain",
            "data-id": dashboard.ID
        }, collapsingWrapper);
        
        dashboard.columnsContainerNode = kanbanInnerMain;

        

        for (let key in this.columnsReg) {
            let columnTemplate = this.columnsReg[key];

            const kanbanContentContainer = this.UiModder.domConstruct.create("div", {
                className: "kanbanContentContainer",
                "data-id": key,
                "dashboard-id": dashboard.ID
            }, kanbanInnerMain);
            this.UiModder.domClass.add(kanbanContentContainer, "kanbanContentContainer");

            if (columnTemplate.width) {
                kanbanContentContainer.style.setProperty("--columnWidth", columnTemplate.width);
            }

            let columnReadonly = this.readonly;
            if (!columnReadonly) {
                columnReadonly = dashboard.readonly === true;
                if (!columnReadonly) {
                    columnReadonly = columnTemplate.readonly === true;
                }
            }

            this.UiModder.domClass.toggle(kanbanContentContainer, "readonly", columnReadonly);

            //const kanbanTopHeader = this.UiModder.domConstruct.create("div");
            //this.UiModder.domClass.add(kanbanTopHeader, "kanbanTopBorder");
            //this.UiModder.domConstruct.place(kanbanTopHeader, kanbanContentContainer);

            const kanbanContentHeader = this.UiModder.domConstruct.create("div");
            this.UiModder.domClass.add(kanbanContentHeader, "kanbanContentHeader");
            kanbanContentHeader.innerHTML = columnTemplate.label;
            this.UiModder.domConstruct.place(kanbanContentHeader, kanbanContentContainer);

            const kanbanContent = this.UiModder.domConstruct.create("div", {
                className: "kanbanContent",
                "data-id": columnTemplate.ID,
                "dashboard-id": dashboard.ID
            }, kanbanContentContainer);

            let column: KanbanTypes.Column = {
                ...columnTemplate,
                node: kanbanContentContainer,
                contentNode: kanbanContent,
                dataElements: [],
                dashboard: dashboard,
                _sortable: null,
                isSourceColumn: false
            }

            if (dashboard.columnLabels && dashboard.columnLabels[column.ID]) {
                let customLabels = dashboard.columnLabels[column.ID];
                if (customLabels.label) column.label = customLabels.label;
                if (customLabels.secondaryLabel) column.secondaryLabel = customLabels.secondaryLabel;
            }

            if (dashboard.existingAssignments) {
                let existingDataForColumn = dashboard.existingAssignments.filter(entry => entry.columnID == column.ID);

                for (let dataTemplate of existingDataForColumn) {

                    let extendedData: KanbanTypes.DataElement = {
                        ...dataTemplate,
                        node: null,
                        actionContainerNode: null,
                        column: column,
                        wasMoved: false
                    }

                    if (!extendedData.assignmentID) {
                        extendedData.assignmentID = column.ID + "-" + extendedData.sourceItemID;
                    }

                    this.drawDataElement(extendedData, column);
                    //if (extendedData.assignmentID == "00000135:0002a85d") debugger;
                    this.dataStore.addEntry({
                        assignmentID: extendedData.assignmentID,
                        sourceItemID: extendedData.sourceItemID,
                        columnID: column.ID,
                        order: column.dataElements.length,
                    });

                    column.dataElements.push(extendedData);

                }
            }
            dashboard.columnsReg[key] = column;

            this._createColumnSortable(column);

        }
    }
    private _createColumnSortable(column: KanbanTypes.Column): void {
        let dashboard = column.dashboard;

        let bAllowSorting = column.allowSorting === true;
        if (!bAllowSorting) {
            if (column.allowSorting !== false) {
                bAllowSorting = column.dashboard.allowSorting === true;
            }
        }

        let bReadonly = this.readonly || column.readonly || column.dashboard.readonly;
       
        column._sortable = Sortable.create(column.contentNode, {
            animation: 150,
            sort: !bReadonly && bAllowSorting,
            draggable: ".kanbanDataElement.draggable",
            ghostClass: "dragoverTemplate",
            chosenClass: "dragging",
            group: {
                name: dashboard.ID,
                //revertClone: true,
                put: bReadonly ? false : lang.hitch(this, this._checkDragTarget, dashboard), // bei readonly disablen, ansonsten nur aus gleiche Gruppe zulassen
            },
            onAdd: lang.hitch(this, this._onSortableAdd_ToColumn, dashboard),
            onEnd: lang.hitch(this, this._onSortableEnd_ToColumn, dashboard),
        });
    }

    private _duplicateHighlight: { timeout: any, sourceItemID: string, targetColumnID: string, node: HTMLElement };
    private _checkDragTarget(dashboard: KanbanTypes.Dashboard, to, from, draggedElement: HTMLElement): boolean {

        let sourceElement = from.el as HTMLElement;
        let targetElement = to.el as HTMLElement;
        //console.debug(this.id + " _checkDragTarget", from, to);
        if (!targetElement || !targetElement.getAttribute) {
            //console.debug(this.id + " _checkDragTarget: ABORT - INVALID TARGET", to);
            return false;
        }
        if (!sourceElement || !sourceElement.getAttribute) {
            //console.debug(this.id + " _checkDragTarget: ABORT - INVALID SOURCE", from);
            return false;
        }

        let targetColumn = this.getColumnOfDashboard(dashboard, targetElement.getAttribute("data-id"));
        let sourceColumn = this.getColumnOfDashboard(dashboard, sourceElement.getAttribute("data-id"));
        let sourceDashboard = this.dashboardsReg[sourceElement.getAttribute("dashboard-id")]

        //console.debug(this.id + " _checkDragTarget: ok?", sourceDashboard, dashboard, sourceColumn);


        if (dashboard.ID != sourceDashboard.ID) {
            //console.debug(this.id + " _checkDragTarget: ABORT - FOREIGN COLUMN!", dashboard.ID + " < vs. > " + sourceDashboard.ID);
            return false;
        }

        let duplicateDataElement = this.getDataBySourceId(targetColumn, draggedElement.getAttribute("data-id"));

        if (duplicateDataElement) {
            if (this._duplicateHighlight) {
                clearTimeout(this._duplicateHighlight.timeout);
                // wenns dasselbe item bzw die Column betrifft passts
                if (this._duplicateHighlight.sourceItemID == duplicateDataElement.sourceItemID && this._duplicateHighlight.targetColumnID == targetColumn.ID) {
                    
                } else { // wenns ein anderes betrifft, dann das alte unbedingt manuell wieder vom Style befreien!
                    this.UiModder.domClass.remove(this._duplicateHighlight.node, "duplicate");
                }
            }

            this.UiModder.domClass.add(duplicateDataElement.node, "duplicate");
            this._duplicateHighlight = {
                timeout: null,
                node: duplicateDataElement.node,
                sourceItemID: duplicateDataElement.sourceItemID,
                targetColumnID: targetColumn.ID
            };

            this._duplicateHighlight.timeout = setTimeout(lang.hitch(this, function (node) {
                this._duplicateHighlight = null;
                try {
                    this.UiModder.domClass.remove(node, "duplicate");
                    
                }
                catch (ex) {

                }
            }, duplicateDataElement.node), 500);

            return false;
        }

        return true;
    }
    private _createInfoColumnNode(dashboard: KanbanTypes.Dashboard, config: KanbanTypes.DashboardConfig) {

        if (config.infoColumn) {
            dashboard.infoColumn = {
                ...config.infoColumn,
                node: null
            }
            this.UiModder.domClass.add(dashboard.node, "showInfoColumn");
        }

        const infoColumnNode = this.UiModder.domConstruct.create("div", {
            className: "kanbanAside infoColumn",
        }, dashboard.innerContainerNode, "last");

        
        infoColumnNode.style.setProperty("--columnWidth", this.infoColumnWidth);
        

        //const kanbanHeader = this.UiModder.domConstruct.create("div", {
        //    className: "kanbanHeader",
        //}, kanbanAside);
        const collapsing = this.UiModder.domConstruct.create("div", {
            className: "collapsing"
        }, infoColumnNode);
       
        const collapsingWrapper = this.UiModder.domConstruct.create("div", {
            className: "collapsingWrapper"
        }, collapsing);

        if (dashboard.infoColumn) {
            this.UiModder.domClass.add(this.dashboardsContainerNode, "useInfoColumns");

            const rightArea = this.UiModder.domConstruct.create("div", {
                className: "kanbanAsideArea"
            }, collapsingWrapper);


            this.UiModder.domConstruct.create("label", {
                innerHTML: dashboard.infoColumn.label
            }, rightArea);

            const kanbanAsideContent = this.UiModder.domConstruct.create("div", {
                className: "kanbanAsideContent",
            }, rightArea);



            /* for (let kanbanElement of kanbanContainer.RightAside.KanbanElements) {
                this.createKanbanHTMLElement(kanbanElement, kanbanAsideContent);
            }*/
            // TODO: right side selected kanban element

            dashboard.infoColumn.node = kanbanAsideContent;

            // jetzt müssen alle Dataelements noch 
        }
    }


    private _createDataPoolColumn(config: KanbanTypes.DashboardConfig, dashboard: KanbanTypes.Dashboard): void {
        const datapoolContainer = this.UiModder.domConstruct.create("div", {
            className: "kanbanAside dataPool",
        }, dashboard.innerContainerNode, "first");

        datapoolContainer.style.setProperty("--columnWidth", this.leftWidth);

        const collapsing = this.UiModder.domConstruct.create("div", {
            className: "collapsing"
        }, datapoolContainer);

        const collapsingWrapper = this.UiModder.domConstruct.create("div", {
            className: "collapsingWrapper"
        }, collapsing);


        if (config.sourceData && config.sourceData.dataElements?.length) {
            dashboard.sourceData = {
                dashboard: dashboard,
                label: config?.sourceData.label,
                dataElements: config.sourceData.dataElements as any,
                node: datapoolContainer,
                contentNode: null,
                _sortable: null,
                isSourceColumn: true,
                ID: "datapool-" + config.ID
            };


            dashboard.columnsReg[dashboard.sourceData.ID] = dashboard.sourceData;            

            const datapoolArea = this.UiModder.domConstruct.create("div", {
                className: "kanbanAsideArea"
            }, collapsingWrapper);

            this.UiModder.domConstruct.create("label", {
                innerHTML: dashboard.sourceData.label,
            }, datapoolArea);

            const datapoolContent = this.UiModder.domConstruct.create("div", {
                className: "kanbanAsideContent",
                "data-id": dashboard.sourceData.ID,
                "dashboard-id": dashboard.ID
            }, datapoolArea);

            dashboard.sourceData.contentNode = datapoolContent;


            for (let dataElement of dashboard.sourceData.dataElements) {
                let dataEx = dataElement as KanbanTypes.DataElement;
                dataEx.column = dashboard.sourceData;
                dashboard.dataElements.push(dataEx);
                this.drawDataElement(dataEx, dashboard.sourceData);
            }

            let leftSortable = Sortable.create(dashboard.sourceData.contentNode, {
                sort: false,
                animation: 150,
                draggable: ".kanbanDataElement.draggable",
                chosenClass: "dragging",
                group: {
                    name: dashboard.ID,
                    put: false,
                    //revertClone: true, // macht das, was wir jetzt schon manuell im End-Event machen (Klon und Source switchen)
                    pull: "clone"
                },
                onEnd: lang.hitch(this, this._onSortableEnd_FromDataPool, dashboard),
                onAdd: lang.hitch(this, this._onSortableAdd_ToDataPool, dashboard),
                onClone: lang.hitch(this, this._onSortableClone_ToDataPool, dashboard)
            });

            dashboard.sourceData._sortable = leftSortable;
        }
    }
    private _onSortableClone_ToDataPool(dashboard: KanbanTypes.Dashboard, evt: SortableEvent) {
        // nix
        if (evt.from == evt.to) return;
        //console.debug(this.id + " _onSortableClone_DataPool", "from: " + evt.from.dataset.id, "to: " + evt.to.dataset.id, dashboard, evt);
    }


    private _onSortableEnd_ToColumn(dashboard: KanbanTypes.Dashboard, evt: SortableEvent) {
        console.debug(this.id + " _onSortableEnd_ToColumn", "from: " + evt.from.dataset.id, "to: " + evt.to.dataset.id, dashboard, evt);

        
        const columnSource = this.getColumnOfDashboard(dashboard, evt.from.dataset.id);
        const columnTarget = this.getColumnOfDashboard(dashboard, evt.to.dataset.id);
                

        if (columnSource) {
            const dataElement = this.getDataBySourceId(columnTarget, evt.item.dataset.id);
            
            //ToasterHelpers.showToast("_onSortableEnd" + " | elem: " + element.label + " | from col: " + columnSource.label);

            if (dataElement !== null) {
                let storeData = this.dataStore.getEntityById(dataElement.assignmentID);
                if (columnSource == columnTarget) {
                    if (evt.oldIndex != evt.newIndex) {
                        // umsortiert worden!
                        columnSource.dataElements.splice(evt.oldIndex, 1);
                        columnSource.dataElements.splice(evt.newIndex, 0, dataElement);
                        this._sortKanbanById(columnSource);

                        dataElement.order = evt.newIndex;

                        storeData.order = evt.newIndex;
                        this.dataStore.changeEntry(storeData);
                    }
                } else {
                    storeData.columnID = columnTarget.ID;
                    this.dataStore.changeEntry(storeData);
                }
            }
        }
    }
    private _onSortableEnd_FromDataPool(dashboard: KanbanTypes.Dashboard, evt: SortableEvent) {
        console.debug(this.id + " _onSortableEnd_FromDataPool", "from: " + evt.from.dataset.id, "to: " + evt.to.dataset.id, dashboard, evt);
        if (evt.from == evt.to) return;

        const columnSource = this.getColumnOfDashboard(dashboard, evt.from.dataset.id);
        const columnTarget = this.getColumnOfDashboard(dashboard, evt.to.dataset.id);

        let movedElementOriginal = this.getDataBySourceId(columnTarget, evt.item.dataset.id);
        let movedElementClone = this.getDataBySourceId(columnSource, evt.item.dataset.id); // nodejs klont das eigentlich verschobene Element an den ursprünglichen Ort!

        if (columnSource.isSourceColumn == true ) {
            //this.UiModder.domClass.add(evFt.clone, "disabled");

            if (dashboard.allowMultiAssign != true) {
                //ToasterHelpers.showToast("_onSortableEnd_DataPool" + " | elem: " + movedElementOriginal.label + " | from col: " + columnSource?.label + " | to col: " + columnTarget?.label);
                if (movedElementOriginal) {
                    this.setDataElementToInUse(dashboard.sourceData, movedElementOriginal.sourceItemID);
                }
            }
            //this.setDataElementToInUse(dashboard, evt.clone.dataset.id);

        }
    }

    private _onSortableAdd_ToColumn(dashboard: KanbanTypes.Dashboard, evt: SortableEvent) {

        // ein Element wurde in eine column hineingezogen
        // möglicherweise aus einer anderen column, möglicherweise aus dem Datapool

        console.debug(this.id + " _onSortableAdd_ToColumn", "from: " + evt.from.dataset.id, "to: " + evt.to.dataset.id, dashboard, evt);
        if (evt.from == evt.to) return;
        

        const columnSource = this.getColumnOfDashboard(dashboard, evt.from.dataset.id);
        const columnTarget = this.getColumnOfDashboard(dashboard, evt.to.dataset.id);


        // gibts dieses Element schon in dieser Spalte??
        let element = this.getDataBySourceId(columnTarget, evt.item.dataset.id);
        if (element) {
            if (columnTarget.allowMultiAssign != true) {
                // erneutes Hinzufügen in die Spalte nicht erlaubt
                // sortable hat uns aber schon die node geklont etc
                // was tun?
                alert("duplicate assignment: alement already in column!");
                return;
            }
        }

        if (columnSource.isSourceColumn == true && evt.pullMode === "clone") {
            this._sortable_CloneKanbanElement(dashboard, evt);
            // event.item.addEventListener("click", lang.hitch(this, this.selectKanbanElement));
        } else {
            this._sortable_MoveDataElement(dashboard, evt);
        }
    }
    private _onSortableAdd_ToDataPool(dashboard: KanbanTypes.Dashboard, evt: SortableEvent) {
        //console.debug(this.id + " _onSortableAdd_DataPool", "from: " + evt.from.dataset.id, "to: " + evt.to.dataset.id, dashboard, evt);
        if (evt.from == evt.to) return;

        /* 
        Element wurde aus einer anderen Column in diesen Datapool verschoben
        nicht zulassen - soll Remove-Button verwenden, ist eindeutiger in Handhabung
        */

        const columnSource = this.getColumnOfDashboard(dashboard, evt.from.dataset.id);
        const columnTarget = this.getColumnOfDashboard(dashboard, evt.to.dataset.id);

        //ToasterHelpers.showToast("_onSortableAdd" + " | from col: " + columnSource?.label + " | to col: " + columnTarget?.label);

        if (evt.pullMode && evt.pullMode === "clone") {
            this._sortable_CloneKanbanElement(dashboard, evt);
            // event.item.addEventListener("click", lang.hitch(this, this.selectKanbanElement));
        } else {
            this._sortable_MoveDataElement(dashboard, evt);
        }
    }

    private _onStoreChange(data) {
        if (this._isSettingUp) return;
        if (this.readonly) return;

        if (this.dataChangeCallback) {
            this.dataChangeCallback(this.dataStore.getDiffData(true));
        }
    }

    private _isSettingUp: boolean = true;
    setup(config: KanbanTypes.MainConfig) {
        this._isSettingUp = true;

        this.columnsReg = {};
        this.dashboardsReg = {};
        this._currentInfoContentRequest = {};

        if (config.dataChangeCallback) this.dataChangeCallback = config.dataChangeCallback;
        if (config.showDetailsCallback) this.dataDetailsCallback = config.showDetailsCallback;
        if (config.editorRequestCallback) this.editorRequestCallback = config.editorRequestCallback;
        if (config.editBtnEnabledCallback) this.editBtnEnabledCallback = config.editBtnEnabledCallback;
        if (config.deleteBtnEnabledCallback) this.deleteBtnEnabledCallback = config.deleteBtnEnabledCallback;
        if (config.infoContentAPI) this.infoContentAPI = config.infoContentAPI;


        this.dataStore = new EntityStore();
        this.dataStore.keyAttribute = "assignmentID";
        this.dataStore.on("change", lang.hitch(this, this._onStoreChange));


        if (config.dataPoolWidth) this.leftWidth = config.dataPoolWidth;
        if (config.infoColumnWidth) this.infoColumnWidth = config.infoColumnWidth;
        if (config.contentHeight) this.defaultContainerHeight = config.contentHeight;

        this.domNode.style.setProperty("--contentHeight", this.defaultContainerHeight);


        // die columns bleiben insgesamt für alle Dashbaords gleich und haben deshalb ihre eigene Base-Registry
        for (let columnConfig of config.columns) {
           
            this.columnsReg[columnConfig.ID] = columnConfig;

        }

        this.UiModder.domConstruct.empty(this.dashboardsContainerNode);

        
        // für jedes Dashboard:
        for (let dashboardConfig of config.dashboards) {
            //let dashboardConfigClone = lang.clone(dashboardConfig);
            let dashboard: KanbanTypes.Dashboard = {
                ...dashboardConfig,
                dataElements: [],
                columnsReg: {},
                expanderNode: null,
                isExpanded: true,
                innerContainerNode: null,
                columnsContainerNode: null,
                scrollingContainer: null,
                node: null,
                sourceData: null,
                infoColumn: null
            }

            
            this.dashboardsReg[dashboard.ID] = dashboard;

            // container erzeugen
            const kanbanContainerHTML = this.UiModder.domConstruct.create("div", {
                className: "kanbanContainer"
            }, this.dashboardsContainerNode);


            dashboard.node = kanbanContainerHTML;

            this.UiModder.domClass.toggle(dashboard.node, "readonly", dashboard.readonly === true);

            const kanbanHeader = this.UiModder.domConstruct.create("div", {
                className: "hoverDark kanbanCollapseButton kanbanHeader",
            }, kanbanContainerHTML);

            let kanbanLabelContainer = this.UiModder.domConstruct.create("div", {
                className: "labelContainer",
            }, kanbanHeader);

            this.UiModder.domConstruct.create("label", {
                innerHTML: dashboard.label
            }, kanbanLabelContainer);


            const innerContainer = this.UiModder.domConstruct.create("div", {
                className: "kanbanInnerContainer"
            }, kanbanContainerHTML);

            dashboard.innerContainerNode = innerContainer;


            if (dashboard.containerHeight) {
                //this.UiModder.domStyle.set(kanbanContentContainer, "height", "calc(" + dashboard.containerHeight + " - (var(--containerHeaderHeight) + 21px))");
                dashboard.innerContainerNode.style.setProperty("--contentHeight", dashboard.containerHeight);
            }
           
            this._createInfoColumnNode(dashboard, dashboardConfig);


            this._createKanbanContainerMain(dashboard);
           
            

            // linken und rechten bereich befüllen/erzeugen
            this._createDataPoolColumn(dashboardConfig, dashboard);
            if (dashboardConfig.sourceData) {
                this.UiModder.domClass.add(dashboard.node, "showDataPool");


                if (dashboard.allowMultiAssign != true && dashboard.existingAssignments?.length) {
                    
                    for (let assignment of dashboard.existingAssignments) {
                        this.setDataElementToInUse(dashboard.sourceData, assignment.sourceItemID);
                    }
                }

            }

            
           

            this._createExpander(dashboard);

        }

        this.dataStore.commit();

        setTimeout(lang.hitch(this, function() { this._isSettingUp = false; }), 200);
    }


    private _createExpander(dashboard: KanbanTypes.Dashboard) {
        

        let firstHeader = dashboard.node.querySelector(".kanbanHeader");

        const collapseIconContainer = this.UiModder.domConstruct.create("div", {
            className: "collapseIconContainer",
            "data-id": dashboard.ID
        }, firstHeader);

        on(firstHeader, "click", lang.hitch(this, this._onClick_CollapseKanban, dashboard));

        HtmlHelpers.createFontawesomeIcon("fas fa-chevron-up expandBtnIcon", null, collapseIconContainer);

        //let expander = new ExpandableSection();
        //expander.setupAsReplacement(dashboard.node as any, dashboard.label, "small");
        
    }
    private _collapseDashboard(dashboard: KanbanTypes.Dashboard, bDoCollapse?: boolean): void {

        let expanderBtn = dashboard.expanderNode;
        let innerContainer = dashboard.innerContainerNode;

        if (typeof (bDoCollapse) != "boolean") {
            bDoCollapse = dashboard.isExpanded == true;
        }

        dashboard.isExpanded = bDoCollapse !== true;

        this.UiModder.domClass.toggle(dashboard.node, "collapsed", !dashboard.isExpanded);
        this.UiModder.domClass.toggle(innerContainer, "collapsed", !dashboard.isExpanded);
    }

    private _onClick_CollapseKanban(dashboard: KanbanTypes.Dashboard, event: PointerEvent): void {
        event.preventDefault();

        this._collapseDashboard(dashboard);
    }

    private _scrollHandler(sourceID: string, event: Event): void {
        let target = (<HTMLElement>event.target);

        let sourceDashboard = this.dashboardsReg[sourceID];
        target = sourceDashboard.scrollingContainer;

        for (let id in this.dashboardsReg) {
            let dashboard = this.dashboardsReg[id];
            if (dashboard.ID !== sourceID) {
                dashboard.scrollingContainer.scrollLeft = target.scrollLeft;
            }
        }
    }

    //BASE
    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        this.useStandbyManager = true;
    }
    startup(): void {
        super.startup.apply(this, arguments as any);
    }
}

export default mdsWidget(Kanban);